import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { getElasticSearchURLS } from "@selectors/enviromentSelectors";
import { getAllResponsiblesState } from "@selectors/responsiblesSelectors";
import PersesSearchAPI from "@services/persesSearchService";
import activitiesTypes from "@utils/constants/activities/activitiesTypes";
import {
  DASHBOARD_RANGE_TYPES_DICTIONARY,
  DASHBOARD_RANGE_TYPE_KEYS,
  QUERY_STATUS,
} from "@utils/constants/dashboard/dashboardModuleConstants";
import { CalendarActivityTypes } from "@customTypes/filter";
import { LawsuitResponsible } from "@customTypes/user/responsible";
import { InProgressActivitiesByResponsibleResponse } from "@customTypes/services/responses";

type InProgressActivitiesParserResponse = {
  series: {
    name: string;
    data: number[];
  }[];
  responsible: string[];
  noData: boolean;
};

const parseInProgressActivitiesByResponsible = (
  response: InProgressActivitiesByResponsibleResponse = [],
  responsibleArray: LawsuitResponsible[],
  responsibleFilter: string[],
  calendarActivityTypes: CalendarActivityTypes[] = []
): InProgressActivitiesParserResponse => {
  const noData = response.length === 0;
  const selectedResponsible = responsibleArray.filter((responsible) =>
    responsibleFilter.includes(responsible.id)
  );
  const selectedResponsibleNicknames = selectedResponsible.map((r) => r.nickname);
  const selectedResponsibleIds = selectedResponsible.map((r) => r.id);

  const series = calendarActivityTypes.map((activityType) => {
    const data = selectedResponsibleIds.map((responsibleId) => {
      const activityInBucket = response.find(
        (bucket) => bucket.secondKey === +responsibleId && bucket.firstKey === activityType
      );

      return activityInBucket ? activityInBucket.count : 0;
    });

    return { name: activitiesTypes[activityType].pluralLabel, data };
  });

  return { series, responsible: selectedResponsibleNicknames, noData };
};

const useInProgressActivitiesByResponsibleStackedColumnChartQuery = (
  rangeFilter: DASHBOARD_RANGE_TYPE_KEYS,
  responsibleFilter: string[],
  calendarActivityTypes: CalendarActivityTypes[]
): [string, InProgressActivitiesParserResponse] => {
  const [inProgressActivities, setInProgressActivities] =
    useState<InProgressActivitiesParserResponse>({ responsible: [], series: [], noData: true });
  const [status, setStatus] = useState(QUERY_STATUS.loading);

  const responsibleArray = useSelector(getAllResponsiblesState);
  const { fromDate, toDate } = DASHBOARD_RANGE_TYPES_DICTIONARY[rangeFilter].range;

  const PERSES_CODED_URLS = useSelector(getElasticSearchURLS);
  const perses = new PersesSearchAPI(PERSES_CODED_URLS);

  const { data, isSuccess, isLoading, isFetching } = useQuery(
    ["inProgressActivities", { responsibleFilter, fromDate, toDate }],
    /* istanbul ignore next */
    () =>
      perses
        .getInProgressActivitiesByResponsible(responsibleFilter, fromDate, toDate)
        .then(({ buckets }) => buckets),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );

  useEffect(() => {
    if (isLoading) return;
    if (isFetching) {
      setStatus(QUERY_STATUS.refeching);
      return;
    }
    if (isSuccess) {
      const parsedData = parseInProgressActivitiesByResponsible(
        data,
        responsibleArray,
        responsibleFilter,
        calendarActivityTypes
      );
      setInProgressActivities(parsedData);
      setStatus(QUERY_STATUS.success);
    }
  }, [
    data,
    isSuccess,
    responsibleArray,
    responsibleFilter,
    isLoading,
    isFetching,
    calendarActivityTypes,
  ]);

  return [status, inProgressActivities];
};

export default useInProgressActivitiesByResponsibleStackedColumnChartQuery;
